import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Wifi, Check, MapPin, ArrowRight, Shield, Zap, Star } from 'lucide-react';

const providers = [
  { name: 'Telekom', speed: 'Bis 1.000 Mbit/s', badge: 'Bestes Netz' },
  { name: 'Vodafone', speed: 'Bis 1.000 Mbit/s', badge: 'Kabel & Glasfaser' },
  { name: 'O2', speed: 'Bis 1.000 Mbit/s', badge: 'Günstig' },
  { name: '', speed: 'Bis 1.000 Mbit/s', badge: 'Flexibel' },
];

const technologies = [
  { name: 'Glasfaser (FTTH)', speed: 'Bis 1.000 Mbit/s', desc: 'Die schnellste und zukunftssicherste Technologie', available: 'Im Ausbau' },
  { name: 'Kabel (DOCSIS)', speed: 'Bis 1.000 Mbit/s', desc: 'Hohe Geschwindigkeit über das TV-Kabelnetz', available: 'Weit verbreitet' },
  { name: 'DSL (VDSL)', speed: 'Bis 250 Mbit/s', desc: 'Zuverlässig und fast überall verfügbar', available: 'Flächendeckend' },
];

const faqs = [
  {
    q: 'Welches Internet ist in Saarlouis verfügbar?',
    a: 'In Saarlouis sind DSL, Kabel-Internet und teilweise Glasfaser verfügbar. Die genaue Verfügbarkeit hängt von Ihrer Adresse ab.',
  },
  {
    q: 'Wie schnell ist Internet in Saarlouis?',
    a: 'Je nach Technologie sind bis zu 1.000 Mbit/s möglich. DSL erreicht bis zu 250 Mbit/s, Kabel und Glasfaser bis zu 1.000 Mbit/s.',
  },
  {
    q: 'Welcher Internetanbieter ist in Saarlouis am günstigsten?',
    a: 'Das hängt von Ihrer gewünschten Geschwindigkeit ab. Wir vergleichen alle Anbieter für Ihre Adresse kostenlos.',
  },
  {
    q: 'Gibt es Glasfaser in Saarlouis?',
    a: 'Glasfaser wird in Saarlouis stetig ausgebaut. Prüfen Sie Ihre Adresse bei uns, um die Verfügbarkeit zu klären.',
  },
];

const InternetSaarlouisPage: React.FC = () => {
  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>Internet Saarlouis - DSL, Kabel & Glasfaser vergleichen | Easy Vertrag</title>
        <meta name="description" content="Internet in Saarlouis - vergleichen Sie DSL, Kabel und Glasfaser Tarife. Telekom, Vodafone, O2. Schnell und günstig. Kostenlos beraten lassen!" />
        <meta name="keywords" content="Internet Saarlouis, DSL Saarlouis, Glasfaser Saarlouis, Kabel Internet Saarlouis, Internetanbieter Saarland" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://easy4vertrag.de/internet-saarlouis" />
        <meta property="og:title" content="Internet Saarlouis - DSL, Kabel & Glasfaser | Easy Vertrag" />
        <meta property="og:description" content="Internet in Saarlouis vergleichen. Alle Anbieter, beste Preise." />
        <meta property="og:url" content="https://easy4vertrag.de/internet-saarlouis" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              ${faqs.map(f => `{
                "@type": "Question",
                "name": "${f.q}",
                "acceptedAnswer": {"@type": "Answer", "text": "${f.a}"}
              }`).join(',')}
            ]
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://easy4vertrag.de/"},
              {"@type": "ListItem", "position": 2, "name": "Internet Saarlouis", "item": "https://easy4vertrag.de/internet-saarlouis"}
            ]
          }
        `}</script>
      </Helmet>

      <section className="bg-gradient-to-br from-blue-700 to-blue-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white bg-opacity-15 rounded-full px-4 py-2 text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              Saarlouis · Saarland
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-5 leading-tight">
              Internet in Saarlouis
            </h1>
            <p className="text-xl text-blue-100 mb-4">
              DSL, Kabel oder Glasfaser – vergleichen Sie alle Internetanbieter für Saarlouis und sparen Sie bis zu 40%.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/internet-zuhause" className="inline-flex items-center justify-center px-7 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors">
                Internet vergleichen <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <a href="https://wa.me/491797473491" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-7 py-3 border-2 border-white bg-transparent text-white font-semibold rounded-lg hover:bg-white hover:text-blue-700 transition-colors">
                WhatsApp Beratung
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-3">Internet-Technologien in Saarlouis</h2>
            <p className="text-gray-500 text-center mb-10 max-w-2xl mx-auto">
              Welche Technologie an Ihrer Adresse verfügbar ist, hängt vom Ausbau ab. Wir prüfen das kostenlos für Sie.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {technologies.map((tech, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <Wifi className="w-8 h-8 text-blue-600 mb-3" />
                  <h3 className="font-bold text-gray-800 mb-1">{tech.name}</h3>
                  <p className="text-blue-600 font-semibold text-sm mb-2">{tech.speed}</p>
                  <p className="text-gray-600 text-sm mb-3">{tech.desc}</p>
                  <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">{tech.available}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-10">Internetanbieter für Saarlouis</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {providers.map((p, i) => (
                <div key={i} className="border-2 rounded-xl p-5 text-center bg-white">
                  <p className="text-xl font-bold text-gray-800 mb-2">{p.name}</p>
                  <p className="text-sm text-gray-600 mb-2">{p.speed}</p>
                  <span className="text-xs font-medium text-blue-700 bg-blue-100 px-2 py-1 rounded-full">{p.badge}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-10">Häufige Fragen zu Internet in Saarlouis</h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-800 mb-2 text-base">{faq.q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-blue-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Jetzt Internet in Saarlouis vergleichen</h2>
            <p className="text-blue-100 mb-8">Kostenlose Beratung – wir finden den besten Internetanschluss für Sie.</p>
            <Link to="/internet-zuhause" className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition-colors">
              Internet vergleichen <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InternetSaarlouisPage;
